import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { getJobPortalAuthConfig, getJobPortalSession } from "../../../helpers/jobPortalAuth";
import { applicationStatusOptions } from "../../../helpers/jobPortalMeta";

const STATUS_STYLES = {
  0: { bg: "#f3f5f9", color: "#5f6777", label: "Pending" },
  1: { bg: "#eef4ff", color: "#1d72fa", label: "Under Review" },
  2: { bg: "#fff4e8", color: "#c96a07", label: "Shortlisted" },
  3: { bg: "#e8fbff", color: "#0f9fbd", label: "Interview" },
  4: { bg: "#eaf8ef", color: "#16803c", label: "Selected" },
  5: { bg: "#fff0f1", color: "#cc3344", label: "Rejected" },
  6: { bg: "#f3f5f9", color: "#5f6777", label: "Withdrawn" },
};

const METRIC_CONFIG = [
  { key: "applied", label: "Applied Jobs", icon: "bi-send-fill" },
  { key: "shortlisted", label: "Shortlisted", icon: "bi-stars" },
  { key: "saved", label: "Saved Jobs", icon: "bi-bookmark-heart-fill" },
];

const getInitials = (value) =>
  String(value || "CO")
    .split(" ")
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const SkeletonCard = () => (
  <div className="application-theme-item placeholder-glow">
    <div className="d-flex gap-3">
      <span className="placeholder rounded-4" style={{ width: 56, height: 56 }} />
      <div className="flex-grow-1">
        <span className="placeholder col-5 rounded d-block mb-2" />
        <span className="placeholder col-4 rounded d-block mb-3" />
        <span className="placeholder col-8 rounded d-block" />
      </div>
    </div>
  </div>
);

const SeekerApplications = () => {
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [savedJobs, setSavedJobs] = useState([]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      await getJobPortalSession();
      const authConfig = await getJobPortalAuthConfig();
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (statusFilter !== "") params.set("status", String(statusFilter));

      const [applicationsResponse, savedJobsResponse] = await Promise.all([
        axiosInstance.get(`/job-portal/applications/me?${params.toString()}`, authConfig),
        axiosInstance.get("/job-portal/jobs/saved", authConfig),
      ]);

      setApplications(applicationsResponse.data?.data?.applications || []);
      setSavedJobs(savedJobsResponse.data?.data?.jobs || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load application history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  const shortlistedCount = useMemo(
    () => applications.filter((item) => [2, 3, 4].includes(item.status)).length,
    [applications]
  );

  const metricValues = {
    applied: applications.length,
    shortlisted: shortlistedCount,
    saved: savedJobs.length,
  };

  const withdrawApplication = async (applicationId) => {
    try {
      const authConfig = await getJobPortalAuthConfig();
      await axiosInstance.put(`/job-portal/applications/${applicationId}/withdraw`, {}, authConfig);
      toast.success("Application withdrawn");
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to withdraw application");
    }
  };

  return (
    <>
      <style>{`
        .application-theme-shell {
          background: linear-gradient(180deg, #f4f7fb 0%, #eef2f8 100%);
          min-height: 100vh;
          padding-bottom: 2rem;
        }
        .application-theme-hero {
          background: #fff;
          border: 1px solid #e7ebf3;
          border-radius: 28px;
          box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
          overflow: hidden;
          margin-bottom: 1.5rem;
        }
        .application-theme-band {
          background: linear-gradient(135deg, #1d72fa 0%, #1664e8 100%);
          color: #fff;
          padding: 1.5rem;
          position: relative;
        }
        .application-theme-band::after {
          background:
            linear-gradient(135deg, transparent 55%, #09111f 55%, #09111f 67%, transparent 67%),
            linear-gradient(135deg, transparent 20%, #6bc6ff 20%, #6bc6ff 34%, transparent 34%),
            linear-gradient(135deg, transparent 72%, #ffffff 72%, #ffffff 81%, transparent 81%);
          content: "";
          inset: 0;
          opacity: 0.9;
          position: absolute;
        }
        .application-theme-band > * { position: relative; z-index: 1; }
        .application-theme-topbar {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          justify-content: space-between;
          padding: 1rem 1.25rem;
        }
        .application-theme-metrics {
          display: grid;
          gap: 1rem;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          margin-bottom: 1.5rem;
        }
        .application-theme-metric {
          background: #fff;
          border: 1px solid #e7ebf3;
          border-radius: 24px;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
          padding: 1.15rem;
        }
        .application-theme-metric-icon {
          align-items: center;
          background: #eef4ff;
          border-radius: 14px;
          color: #1d72fa;
          display: inline-flex;
          font-size: 1rem;
          height: 44px;
          justify-content: center;
          margin-bottom: 0.9rem;
          width: 44px;
        }
        .application-theme-metric strong {
          color: #10151c;
          display: block;
          font-size: 1.7rem;
          font-weight: 800;
          letter-spacing: -0.03em;
          line-height: 1;
          margin-bottom: 0.35rem;
        }
        .application-theme-metric span {
          color: #6c7385;
          font-size: 0.82rem;
          font-weight: 700;
          text-transform: uppercase;
        }
        .application-theme-controls {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          justify-content: space-between;
          margin-bottom: 1rem;
        }
        .application-theme-filter {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          min-width: 220px;
          padding: 0.75rem 1rem;
        }
        .application-theme-item,
        .application-theme-empty {
          background: #fff;
          border: 1px solid #e7ebf3;
          border-radius: 24px;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
          padding: 1.25rem;
        }
        .application-theme-item + .application-theme-item {
          margin-top: 1rem;
        }
        .application-theme-company {
          align-items: center;
          background: linear-gradient(180deg, #eef4ff 0%, #dce9ff 100%);
          border-radius: 16px;
          color: #1d72fa;
          display: inline-flex;
          font-size: 1rem;
          font-weight: 800;
          height: 56px;
          justify-content: center;
          min-width: 56px;
        }
        .application-theme-title {
          color: #10151c;
          font-size: 1.06rem;
          font-weight: 700;
          letter-spacing: -0.02em;
        }
        .application-theme-meta {
          color: #6c7385;
          font-size: 0.84rem;
          margin-top: 0.2rem;
        }
        .application-theme-pill {
          border-radius: 999px;
          display: inline-flex;
          font-size: 0.76rem;
          font-weight: 700;
          gap: 0.45rem;
          padding: 0.42rem 0.8rem;
        }
        .application-theme-note {
          background: #f7f9fc;
          border-left: 3px solid #1d72fa;
          border-radius: 0 12px 12px 0;
          color: #556070;
          font-size: 0.86rem;
          margin-top: 0.9rem;
          padding: 0.85rem 1rem;
        }
        .application-theme-row {
          align-items: start;
          display: flex;
          gap: 1rem;
          justify-content: space-between;
        }
        .application-theme-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          min-width: 140px;
        }
        @media (max-width: 991px) {
          .application-theme-metrics {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 767px) {
          .application-theme-topbar,
          .application-theme-controls,
          .application-theme-row {
            flex-direction: column;
          }
          .application-theme-filter,
          .application-theme-actions,
          .application-theme-actions .btn,
          .application-theme-topbar .btn {
            width: 100%;
          }
        }
      `}</style>

      <Sidebar />
      <main className="content">
        <Navbar />

        <div className="application-theme-shell">
          <div className="container-fluid py-4">
            <div className="application-theme-hero">
              <div className="application-theme-band">
                <div className="text-uppercase fw-bold small mb-2" style={{ letterSpacing: "0.08em" }}>
                  Application history
                </div>
                <h2 className="mb-2 fw-bold">Track every step of your hiring journey</h2>
                <p className="mb-0" style={{ maxWidth: 620, color: "rgba(255,255,255,0.82)" }}>
                  Follow status updates, interview progress, and recruiter notes in one cleaner timeline.
                </p>
              </div>

              <div className="application-theme-topbar">
                <div className="text-muted small">
                  Review submitted roles, stay on top of shortlisted positions, and manage your next move.
                </div>
                <Link to="/job-seeker/saved-jobs" className="btn btn-outline-primary">
                  <i className="bi bi-bookmark-heart me-2" />
                  Saved Jobs
                </Link>
              </div>
            </div>

            <div className="application-theme-metrics">
              {METRIC_CONFIG.map((metric) => (
                <div className="application-theme-metric" key={metric.key}>
                  <span className="application-theme-metric-icon">
                    <i className={`bi ${metric.icon}`} />
                  </span>
                  <strong>{loading ? "--" : metricValues[metric.key]}</strong>
                  <span>{metric.label}</span>
                </div>
              ))}
            </div>

            <div className="application-theme-controls">
              <select
                className="application-theme-filter"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                {applicationStatusOptions.map((option) => (
                  <option key={option.value === "" ? "all" : option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {!loading ? (
                <div className="text-muted small">
                  Showing <strong className="text-dark">{applications.length}</strong> application{applications.length !== 1 ? "s" : ""}
                </div>
              ) : null}
            </div>

            {loading ? (
              <div>
                {Array.from({ length: 4 }).map((_, index) => (
                  <SkeletonCard key={index} />
                ))}
              </div>
            ) : applications.length === 0 ? (
              <div className="application-theme-empty text-center">
                <i className="bi bi-send text-muted" style={{ fontSize: 52, opacity: 0.25 }} />
                <h4 className="mt-3 mb-2">No applications yet</h4>
                <p className="text-muted mb-4">
                  Start applying to active jobs and your progress will appear here.
                </p>
                <Link to="/job-seeker/jobs" className="btn btn-primary">
                  Browse Jobs
                </Link>
              </div>
            ) : (
              applications.map((application) => {
                const job = application.job || {};
                const statusStyle = STATUS_STYLES[application.status] || STATUS_STYLES[0];
                const canWithdraw = ![4, 5, 6].includes(application.status);

                return (
                  <div className="application-theme-item" key={application.id}>
                    <div className="application-theme-row">
                      <div className="d-flex gap-3">
                        <span className="application-theme-company">
                          {getInitials(job.company?.name || job.title)}
                        </span>

                        <div>
                          <div className="application-theme-title">
                            {job.title || "Job title unavailable"}
                          </div>
                          <div className="application-theme-meta">
                            <i className="bi bi-building me-1" />
                            {job.company?.name || "Company"}
                            <span className="mx-2">·</span>
                            <i className="bi bi-geo-alt me-1" />
                            {job.location?.display || "Location TBD"}
                          </div>

                          <div className="d-flex flex-wrap gap-2 mt-3">
                            <span
                              className="application-theme-pill"
                              style={{ background: statusStyle.bg, color: statusStyle.color }}
                            >
                              <i className="bi bi-dot" />
                              {application.statusLabel || statusStyle.label}
                            </span>
                            <span className="application-theme-pill" style={{ background: "#f3f5f9", color: "#556070" }}>
                              Applied{" "}
                              {application.appliedAt
                                ? new Date(application.appliedAt).toLocaleDateString("en-IN", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : "recently"}
                            </span>
                            {application.interviewDate ? (
                              <span className="application-theme-pill" style={{ background: "#e8fbff", color: "#0f9fbd" }}>
                                Interview{" "}
                                {new Date(application.interviewDate).toLocaleDateString("en-IN", {
                                  day: "numeric",
                                  month: "short",
                                })}
                              </span>
                            ) : null}
                          </div>

                          {application.recruiterNotes ? (
                            <div className="application-theme-note">
                              <strong className="me-1">Recruiter note:</strong>
                              {application.recruiterNotes}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="application-theme-actions">
                        {canWithdraw ? (
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => withdrawApplication(application.id)}
                          >
                            Withdraw
                          </button>
                        ) : null}
                        <Link to="/job-seeker/jobs" className="btn btn-outline-primary">
                          Browse More
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default SeekerApplications;
